import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Presentation only — the data hook owns the state and the actions. Two small
// dialogs for the "Refine with AI" pending state:
//   RefinePendingDialog    — a generate button was pressed while the refined
//                            transcript is still unsaved.
//   RefineRegenerateDialog — the refined transcript was just saved and the
//                            consultation already has a report built from the
//                            original text.

const primaryButtonSx = {
  backgroundColor: '#29C0BB',
  '&:hover': { backgroundColor: '#1EA8A3' },
  textTransform: 'none',
  fontWeight: 600,
};

const neutralButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  color: '#6B7280',
  backgroundColor: '#F9FAFB',
  border: '1px solid #D1D5DB',
  '&:hover': { backgroundColor: '#F3F4F6' },
};

const cancelButtonSx = { textTransform: 'none', color: '#6B7280' };

function DialogHeader({ title, onClose }) {
  return (
    <DialogTitle>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
  );
}

export function RefinePendingDialog({ open, onSaveAndRun, onRunOriginal, onCancel }) {
  return (
    <Dialog open={!!open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogHeader title="Refined transcript not saved" onClose={onCancel} />
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          Your AI-refined transcript hasn't been saved yet.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Reports are generated from the saved transcript, so generating now would use the original version.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button onClick={onRunOriginal} sx={neutralButtonSx}>
          Generate with original
        </Button>
        <Button onClick={onSaveAndRun} variant="contained" sx={primaryButtonSx}>
          Save refined & generate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RefineNavigationDialog({ open, isRefining, isPending, busy, onCancel, onContinue }) {
  return (
    <Dialog open={!!open} onClose={() => { if (!busy) onCancel(); }}>
      <DialogTitle>{isRefining ? 'Text refinement is still in progress' : isPending ? 'Refined transcript not saved' : 'Transcript ready to continue'}</DialogTitle>
      <DialogContent>
        {isRefining
          ? 'Keep your current text and stop refinement before continuing. Refined changes will not be applied.'
          : isPending
            ? 'Refinement is complete, but the refined transcript could not be saved. Save it before continuing; if saving fails, your refined text will stay here.'
            : 'Refinement has finished. You can continue with your current text.'}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onCancel}>{isRefining ? 'Wait for refinement' : 'Stay here'}</Button>
        <Button disabled={busy} onClick={onContinue}>{isRefining ? 'Keep text and continue' : isPending ? 'Save refined text and continue' : 'Continue'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function RefineRegenerateDialog({ open, onRegenerate, onKeep }) {
  return (
    <Dialog open={!!open} onClose={onKeep} maxWidth="sm" fullWidth>
      <DialogHeader title="Regenerate report with refined transcript?" onClose={onKeep} />
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          The current report was generated from the original transcript.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Regenerating creates a new report version and uses 1 credit.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onKeep} sx={neutralButtonSx}>
          Keep current report
        </Button>
        <Button onClick={onRegenerate} variant="contained" sx={primaryButtonSx}>
          Regenerate
        </Button>
      </DialogActions>
    </Dialog>
  );
}
